import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AlertTriangle, FileText, CheckCircle2, User, MessageSquare, Send } from "lucide-react";

/**
 * ITTicketDetailModal — Diálogo de detalle/gestión de un ticket interno IT.
 *
 * Extraído 1:1 desde `pages/ITTicketsPage.js` (el modal más pesado). Muestra badges de
 * tipo/prioridad/estado, título/descripción, metadatos (creador, asignado, resolución),
 * asignación de soporte y cambio de estado (según permisos), resumen de resolución,
 * comentarios (con alta) e historial de actividad.
 *
 * @param {boolean}     props.detailOpen          Apertura del diálogo.
 * @param {Function}    props.setDetailOpen        Setter del flag de apertura.
 * @param {Object|null} props.selectedTicket       Ticket seleccionado (null = sin datos).
 * @param {Object}      props.typeColors           Mapa tipo→clases de color del badge.
 * @param {Object}      props.typeLabels           Mapa tipo→etiqueta legible.
 * @param {Object}      props.priorityColors       Mapa prioridad→clases de color del badge.
 * @param {Object}      props.priorityLabels       Mapa prioridad→etiqueta legible.
 * @param {Object}      props.statusColors         Mapa estado→clases de color del badge.
 * @param {Object}      props.statusLabels         Mapa estado→etiqueta legible.
 * @param {Function}    props.formatDate           (dateStr) => string — formatea fechas.
 * @param {Function}    props.canEdit              (resource) => boolean — permiso de edición.
 * @param {Array}       props.supportUsers         Usuarios asignables (personal de soporte).
 * @param {Function}    props.handleAssignTicket   (ticketId, assigneeId|null) => void — asigna soporte.
 * @param {Function}    props.handleUpdateStatus   (ticketId, status) => void — cambia el estado.
 * @param {string}      props.newComment           Texto del comentario en edición.
 * @param {Function}    props.setNewComment        Setter del comentario.
 * @param {Function}    props.handleAddComment     Añade el comentario al ticket.
 */
export const ITTicketDetailModal = ({
  detailOpen,
  setDetailOpen,
  selectedTicket,
  typeColors,
  typeLabels,
  priorityColors,
  priorityLabels,
  statusColors,
  statusLabels,
  formatDate,
  canEdit,
  supportUsers,
  handleAssignTicket,
  handleUpdateStatus,
  newComment,
  setNewComment,
  handleAddComment,
}) => {
  return (
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              {selectedTicket?.ticket_type === "INCIDENCIA" ? (
                <AlertTriangle className="h-5 w-5 text-red-400" />
              ) : (
                <FileText className="h-5 w-5 text-blue-400" />
              )}
              {selectedTicket?.ticket_number}
            </DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4 mt-4">
              {/* Header info */}
              <div className="flex flex-wrap gap-2">
                <Badge className={`${typeColors[selectedTicket.ticket_type]} border`}>
                  {typeLabels[selectedTicket.ticket_type]}
                </Badge>
                <Badge className={`${priorityColors[selectedTicket.priority]} text-white`}>
                  {priorityLabels[selectedTicket.priority]}
                </Badge>
                <Badge className={`${statusColors[selectedTicket.status]} text-white`}>
                  {statusLabels[selectedTicket.status]}
                </Badge>
              </div>

              {/* Title and description */}
              <div className="bg-[#1B1B4B] rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white">{selectedTicket.title}</h3>
                <p className="text-slate-300 mt-2 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {/* Meta info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-[#1B1B4B] rounded-lg p-3">
                  <p className="text-slate-400">Creado por</p>
                  <p className="text-white">{selectedTicket.created_by_name || "-"}</p>
                  <p className="text-slate-500 text-xs">{formatDate(selectedTicket.created_at)}</p>
                </div>
                <div className="bg-[#1B1B4B] rounded-lg p-3">
                  <p className="text-slate-400">Asignado a</p>
                  <p className="text-white">{selectedTicket.assigned_to_name || "Sin asignar"}</p>
                </div>
                {selectedTicket.resolved_at && (
                  <div className="bg-[#1B1B4B] rounded-lg p-3">
                    <p className="text-slate-400">Resuelto por</p>
                    <p className="text-white">{selectedTicket.resolved_by_name || "-"}</p>
                    <p className="text-slate-500 text-xs">{formatDate(selectedTicket.resolved_at)}</p>
                  </div>
                )}
              </div>

              {/* Assignment - only for users with edit permission */}
              {canEdit("it_tickets") && selectedTicket.status !== "RESUELTO" && (
                <div className="bg-[#1B1B4B] rounded-lg p-4">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Asignar a (Personal de Soporte)
                  </Label>
                  <Select
                    value={selectedTicket.assigned_to_id || "none"}
                    onValueChange={(v) => handleAssignTicket(selectedTicket.id, v === "none" ? null : v)}
                  >
                    <SelectTrigger className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white">
                      <SelectValue placeholder="Seleccionar..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      <SelectItem value="none" className="text-slate-400">Sin asignar</SelectItem>
                      {supportUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id} className="text-white">{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Status change */}
              {canEdit("it_tickets") && selectedTicket.status !== "RESUELTO" && (
                <div className="bg-[#1B1B4B] rounded-lg p-4">
                  <Label className="text-slate-300">Cambiar Estado</Label>
                  <Select
                    value={selectedTicket.status}
                    onValueChange={(v) => handleUpdateStatus(selectedTicket.id, v)}
                  >
                    <SelectTrigger className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      {Object.entries(statusLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Resolution */}
              {selectedTicket.resolution_summary && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <h4 className="text-green-400 font-medium flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Resolución
                  </h4>
                  <p className="text-slate-300 mt-2">{selectedTicket.resolution_summary}</p>
                </div>
              )}

              {/* Comments */}
              <div className="space-y-3">
                <h4 className="text-[#03BEE4] font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Comentarios ({selectedTicket.comments?.length || 0})
                </h4>
                
                {selectedTicket.comments?.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedTicket.comments.map((comment) => (
                      <div key={comment.id} className="bg-[#1B1B4B] rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <span className="text-white font-medium">{comment.user_name}</span>
                          <span className="text-slate-500 text-xs">{formatDate(comment.timestamp)}</span>
                        </div>
                        <p className="text-slate-300 mt-1">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add comment */}
                <div className="flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Escribe un comentario..."
                    className="flex-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                    onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="bg-[#03BEE4] text-white"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Activity log */}
              {selectedTicket.activity_log?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-slate-400 font-medium text-sm">Historial de Actividad</h4>
                  <div className="space-y-1 text-xs">
                    {selectedTicket.activity_log.map((log) => (
                      <div key={log.id} className="flex justify-between text-slate-500">
                        <span>{log.action} - {log.user_name}</span>
                        <span>{formatDate(log.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
};
